import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search, Loader2, AlertCircle, ArrowLeft, Receipt, Users,
  Calendar, DollarSign, ChevronRight, FileText, TrendingUp,
} from 'lucide-react';
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { fetchClients } from '@/pages/parametrizacion/clients/Services/clients.services';
import { getUltimaPorCliente } from './services/liquidaciones.service';
import { formatCurrency, formatDate } from './components/format';

// ─────────────────────────────────────────────────────────────────────
// Helpers locales
// ─────────────────────────────────────────────────────────────────────
const estadoColor = {
  Borrador: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  Liquidada: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  Anulada: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
};

// ─────────────────────────────────────────────────────────────────────
// Lista de clientes con info de última liquidación
// ─────────────────────────────────────────────────────────────────────
const LiquidacionClientesPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [clientes, setClientes] = useState([]);
  const [ultimas, setUltimas] = useState([]); // [{clienteId, lastConsecutivo, lastEstado, lastTotal, lastFechaCreacion, totalLiquidaciones}]
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Mapa por clienteId para lookup O(1)
  const ultimasByCliente = useMemo(() => {
    const m = new Map();
    ultimas.forEach((u) => m.set(String(u.clienteId), u));
    return m;
  }, [ultimas]);

  // Resumen global
  const resumen = useMemo(() => {
    let totalLiquidaciones = 0;
    let totalRecaudado = 0;
    let clientesConLiquidacion = 0;
    ultimas.forEach((u) => {
      totalLiquidaciones += Number(u.totalLiquidaciones || 0);
      if (u.lastEstado === 'Liquidada') {
        totalRecaudado += Number(u.lastTotal || 0);
      }
      clientesConLiquidacion++;
    });
    return { totalLiquidaciones, totalRecaudado, clientesConLiquidacion };
  }, [ultimas]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [clientesRes, ultimasRes] = await Promise.all([
        fetchClients({ limit: 1000, offset: 1, query: { estado: 'Activo' } }),
        getUltimaPorCliente(),
      ]);
      setClientes(clientesRes?.data || []);
      setUltimas(ultimasRes?.data || []);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: e.message });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Lista filtrada
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return clientes;
    return clientes.filter((c) => {
      return (
        (c.nombre || '').toLowerCase().includes(term) ||
        (c.nit || '').toLowerCase().includes(term)
      );
    });
  }, [clientes, search]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="p-4 md:p-4 lg:p-4"
    >
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-border/50">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/financiera')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="bg-emerald-500/15 p-2.5 rounded-lg">
              <Receipt className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <CardTitle className="text-2xl">Liquidación de Clientes</CardTitle>
              <CardDescription className="mt-1">
                Selecciona un cliente para ver sus liquidaciones, generar nuevas o revisar las entregas del período.
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-6 space-y-4">
          {/* Stats globales */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-emerald-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500 font-medium">Clientes con liquidaciones</p>
                <Users className="h-4 w-4 text-emerald-500" />
              </div>
              <p className="text-2xl font-bold mt-0.5 text-emerald-700">
                {resumen.clientesConLiquidacion}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                de {clientes.length} clientes activos
              </p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500 font-medium">Total liquidaciones</p>
                <FileText className="h-4 w-4 text-blue-500" />
              </div>
              <p className="text-2xl font-bold mt-0.5 text-blue-700">
                {resumen.totalLiquidaciones}
              </p>
              <p className="text-xs text-gray-400 mt-1">acumuladas</p>
            </div>
            <div className="bg-violet-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500 font-medium">Última confirmada</p>
                <TrendingUp className="h-4 w-4 text-violet-500" />
              </div>
              <p className="text-2xl font-bold mt-0.5 text-violet-700">
                {formatCurrency(resumen.totalRecaudado)}
              </p>
              <p className="text-xs text-gray-400 mt-1">total de la última</p>
            </div>
          </div>

          {/* Buscador */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar cliente por nombre o NIT..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-10"
            />
          </div>

          {/* Lista */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed">
              <Users className="h-10 w-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">
                {search.trim()
                  ? 'Sin clientes que coincidan con el filtro.'
                  : 'No hay clientes activos para mostrar.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map((c) => {
                const u = ultimasByCliente.get(String(c._id));
                return (
                  <ClienteCard
                    key={c._id}
                    cliente={c}
                    ultima={u}
                    onClick={() => navigate(`/financiera/liquidacion-clientes/${c._id}`)}
                  />
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Card individual de cliente
// ─────────────────────────────────────────────────────────────────────
const ClienteCard = ({ cliente, ultima, onClick }) => {
  const tieneLiquidaciones = !!ultima;
  const ultimaFecha = ultima?.lastFechaLiquidacion || ultima?.lastFechaCreacion;
  return (
    <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.15 }}>
      <Card
        className="cursor-pointer hover:shadow-md border-l-4 border-l-emerald-500 transition-all"
        onClick={onClick}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-base text-gray-900 truncate">
                {cliente.nombre}
              </p>
              {cliente.nit && (
                <p className="text-xs text-gray-500 mt-0.5">NIT {cliente.nit}</p>
              )}
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" />
          </div>

          {tieneLiquidaciones ? (
            <div className="space-y-2 mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-gray-500">Última liquidación</p>
                <Badge className={estadoColor[ultima.lastEstado] || ''}>
                  {ultima.lastEstado}
                </Badge>
              </div>
              <p className="font-mono text-xs text-gray-700">{ultima.lastConsecutivo}</p>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Calendar className="h-3 w-3" />
                  {formatDate(ultimaFecha)}
                </div>
                <p className="text-sm font-bold text-emerald-700">
                  {formatCurrency(ultima.lastTotal)}
                </p>
              </div>
              {ultima.totalLiquidaciones > 1 && (
                <p className="text-xs text-gray-400">
                  {ultima.totalLiquidaciones} liquidaciones acumuladas
                </p>
              )}
            </div>
          ) : (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-400 italic">
                Sin liquidaciones previas
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default LiquidacionClientesPage;
